/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/fields/MultiSelectGuasti.tsx
 * Release: v1.0.0
 * Descrizione: Componente per selezione multipla guasti turbina
 */

import React, { useState } from 'react';

interface GuastoOption {
  value: string;
  label: string;
  icon?: string;
}

interface MultiSelectGuastiProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: GuastoOption[];
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}

export const MultiSelectGuasti: React.FC<MultiSelectGuastiProps> = ({
  value = [],
  onChange,
  options,
  label,
  required = false,
  disabled = false,
  error
}) => {
  const [selectedGuasti, setSelectedGuasti] = useState<Set<string>>(new Set(value));

  const handleToggleGuasto = (guastoValue: string) => {
    const newSelected = new Set(selectedGuasti);
    
    if (newSelected.has(guastoValue)) {
      newSelected.delete(guastoValue);
    } else {
      newSelected.add(guastoValue);
    }
    
    setSelectedGuasti(newSelected);
    onChange(Array.from(newSelected));
  };

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-medium text-slate-300">
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {options.map((option) => {
          const isSelected = selectedGuasti.has(option.value);
          
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleToggleGuasto(option.value)}
              disabled={disabled}
              className={`
                relative px-4 py-3 rounded-lg border-2 transition-all
                ${isSelected 
                  ? 'bg-blue-600/20 border-blue-500 text-white' 
                  : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-slate-500'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${error ? 'border-red-500' : ''}
              `}
            >
              <div className="flex items-center space-x-2">
                {/* Checkbox indicator */}
                <div className={`
                  w-5 h-5 rounded border-2 flex items-center justify-center
                  ${isSelected 
                    ? 'bg-blue-500 border-blue-500' 
                    : 'bg-transparent border-slate-500'
                  }
                `}>
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>

                {/* Icon e Label */}
                <span className="text-lg">{option.icon}</span>
                <span className="font-medium">{option.label}</span>
              </div>

              {/* Badge selected nell'angolo */}
              {isSelected && (
                <div className="absolute top-1 right-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Riepilogo selezioni */}
      {selectedGuasti.size > 0 && (
        <div className="mt-3 p-3 bg-slate-700/50 rounded-lg">
          <div className="text-xs text-slate-400 mb-1">
            Guasti selezionati ({selectedGuasti.size}):
          </div>
          <div className="flex flex-wrap gap-2">
            {Array.from(selectedGuasti).map(guastoValue => {
              const option = options.find(o => o.value === guastoValue);
              return option ? (
                <span 
                  key={guastoValue}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600/20 text-blue-300 rounded text-sm"
                >
                  {option.icon} {option.label}
                  <button
                    type="button"
                    onClick={() => handleToggleGuasto(guastoValue)}
                    className="ml-1 hover:text-red-400"
                    title="Rimuovi"
                  >
                    ×
                  </button>
                </span>
              ) : null;
            })}
          </div>
        </div>
      )}

      {/* Messaggio errore */}
      {error && (
        <div className="text-sm text-red-400">
          {error}
        </div>
      )}
    </div>
  );
};